import * as _ from "lodash";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { EditorState, ContentState, convertToRaw } from "draft-js";

const validator = (rule, errorMessage) => value => {
  if (!rule(value)) {
    return errorMessage;
  }
};

const pattern = (regex, errorMessage = "Invalid Format") =>
  validator(
    value => _.isEmpty(value) || _.isUndefined(value) || regex.test(value),
    errorMessage
  );

const required = validator(
  value =>
    !(
      value === "" ||
      _.isNil(value) ||
      (_.isArray(value) && value.length === 0)
    ),
  "This field is required."
);

const requiredFirstName = validator(
  value =>
    !(
      value === "" ||
      _.isNil(value) ||
      (_.isArray(value) && value.length === 0)
    ),
  "Please enter your first name."
);

const requiredLastName = validator(
  value =>
    !(
      value === "" ||
      _.isNil(value) ||
      (_.isArray(value) && value.length === 0)
    ),
  "Please enter your last name."
);

const requiredRichEditor = validator(
  value =>
    value !==
    draftToHtml(
      convertToRaw(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(htmlToDraft("").contentBlocks)
        ).getCurrentContent()
      )
    ),
  "This field is required."
);

const isEqual = (name, sameName, msg) => (value, allValue) => {
  if (allValue[name] !== allValue[sameName]) {
    return msg || `${sameName} is not equal to ${name}`;
  }
};

const email = msg => pattern(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i, msg);

const alphaNumericSpace = msg => pattern(/^[a-zA-Z0-9 ]+$/, msg);

const alphaSymbolicSpace = msg => pattern(/^[a-zA-Z0-9-,()/’'&. ]+$/, msg);

const alphaSpace = msg => pattern(/^[a-zA-Z ]+$/, msg);

const dropdown = msg => validator(value => value > 0, msg);

const maxLength = (max, msg) =>
  validator(
    value => _.isUndefined(value) || _.isNull(value) || value.length <= max,
    msg
  );

const minLength = (min, msg) =>
  validator(
    value => _.isUndefined(value) || _.isNull(value) || value.length >= min,
    msg
  );

const checkboxChecked = msg =>
  validator(value => _.isUndefined(value) || value === true, msg);

const minNumber = (min, msg) =>
  validator(value => _.toInteger(value) >= min, msg);

const maxNumber = (max, msg) =>
  validator(value => _.toInteger(value) <= max, msg);

const url = msg =>
  pattern(
    /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/,
    msg
  );

const price = msg => pattern(/^\d{0,8}((\.|,)\d{1,4})?$/, msg);

const numeric = msg => pattern(/^[0-9]+$/, msg);

const float = msg => pattern(/^\d+(\.\d+)?$/, msg);

const floatWithComma = msg => pattern(/^\d+([,.]\d+)?$/, msg);

const comma = msg => pattern(/^[0-9]+([.][0-9]+)?$/, msg);

const date = msg =>
  pattern(/(0\d{1}|1[0-2])\/([0-2]\d{1}|3[0-1])\/(19|20)\d{2}/, msg);

const credit = `^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|
  6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|
  3(?:0[0-5]|[68][0-9])[0-9]{11}|
  (?:2131|1800|35[0-9]{3})[0-9]{11}|
  3[47][0-9]{13}|
  (6541|6556)[0-9]{12}|
  389[0-9]{11}|
  3(?:0[0-5]|[68][0-9])[0-9]{11}|
  65[4-9][0-9]{13}|64[4-9][0-9]{13}|
  6011[0-9]{12}|
  (622(?:12[6-9]|
  1[3-9][0-9]|
  [2-8][0-9][0-9]|
  9[01][0-9]|92[0-5])[0-9]{10})|
  63[7-9][0-9]{13}|
  9[0-9]{15}|
  (6304|6706|6709|6771)[0-9]{12,15}|
  (5018|5020|5038|6304|6759|6761|6763)[0-9]{8,15}|
  5[1-5][0-9]{14}|
  (6334|6767)[0-9]{12}|(6334|6767)[0-9]{14}|(6334|6767)[0-9]{15}|
  (4903|4905|4911|4936|6333|6759)[0-9]{12}|
  (4903|4905|4911|4936|6333|6759)[0-9]{14}|
  (4903|4905|4911|4936|6333|6759)[0-9]{15}|
  564182[0-9]{10}|564182[0-9]{12}|
  564182[0-9]{13}|633110[0-9]{10}|
  633110[0-9]{12}|633110[0-9]{13}|
  (62[0-9]{14,17})|
  4[0-9]{12}(?:[0-9]{3})?|
  (?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14})|
  )$`;

const creditCard = msg => pattern(new RegExp(credit), msg);

const cardExpDate = msg => pattern(/^[0-1][0-9][/][0-9][0-9]\b/, msg);

const maxFileSize = (file, maxSize = 0) => {
  if (typeof file !== "object" || file == null || maxSize == 0) {
    return undefined;
  }

  const fileSize = (file.size / 1000 / 1000).toFixed(4); // MB
  if (fileSize > maxSize) {
    return `Your file size is too large. Please add a file that is ${maxSize}MB or smaller.`;
  }

  return undefined;
};

export {
  validator,
  creditCard,
  cardExpDate,
  comma,
  floatWithComma,
  required,
  requiredFirstName,
  requiredLastName,
  requiredRichEditor,
  pattern,
  isEqual,
  email,
  alphaNumericSpace,
  dropdown,
  maxLength,
  minLength,
  checkboxChecked,
  url,
  price,
  numeric,
  date,
  float,
  minNumber,
  maxNumber,
  alphaSymbolicSpace,
  alphaSpace,
  maxFileSize
};



// WEBPACK FOOTER //
// ./src/utils/forms/validators.js